const MAX_RAW_LENGTH = 4096
const messages = {
  INVALID_INPUT: 'La lectura del DNI no es válida. Podés continuar manualmente.',
  INPUT_TOO_LONG: 'La lectura supera el tamaño permitido. Podés continuar manualmente.',
  UNSUPPORTED_FORMAT: 'Formato de DNI no reconocido. Podés continuar manualmente.',
  INVALID_DATA: 'La lectura del DNI está incompleta o contiene datos inválidos. Podés continuar manualmente.',
}
const fail = (code) => ({ ok: false, error: { code, message: messages[code] } })
const normalize = (text) => text.normalize('NFC').replace(/\s+/gu, ' ').trim()
const validName = (text) => /\p{L}/u.test(text) && /^[\p{L}\p{M} '\u2019\u002d\u2010\u2011]+$/u.test(text)

function validDate(text) {
  // "-" is the "/" of a US-layout reader typing into a Spanish keyboard layout.
  const match = /^(\d{2})([/-])(\d{2})\2(\d{4})$/.exec(text)
  if (!match) return false
  const [day, month, year] = [match[1], match[3], match[4]].map(Number)
  if (year < 1 || month < 1 || month > 12 || day < 1) return false
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
  return day <= [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1]
}

/**
 * Interpret delivered text only; this is not a barcode decoder or identity check.
 * @param {unknown} raw
 * @returns {{ok: true, data: {documentNumber: string, lastName: string, firstName: string}, format: 'pdf417-modern'|'pdf417-legacy'} | {ok: false, error: {code: string, message: string}}}
 */
export function parseArgentineDni(raw) {
  if (typeof raw !== 'string') return fail('INVALID_INPUT')
  if (raw.length > MAX_RAW_LENGTH) return fail('INPUT_TOO_LONG')

  // Detect separators BEFORE control cleanup, including ASCII GS and RS.
  // '"' is the "@" of a US-layout reader typing into a Spanish keyboard layout.
  // "@" wins: legacy documents embed "||" inside their trailing field.
  const separators = raw.includes('@') ? ['@'] : ['|', '\x1d', '\x1e', '"'].filter((separator) => raw.includes(separator))
  if (separators.length !== 1) return fail('UNSUPPORTED_FORMAT')
  // Only framing controls at the edges are disposable. Embedded controls must
  // never concatenate digits or silently change the meaning of a name.
  const text = raw.replace(/^[\s\x00-\x1c\x1f\x7f]+|[\s\x00-\x1c\x1f\x7f]+$/gu, '')
  const fields = text.split(separators[0]).map(normalize)
  const modern = fields.length === 8 || fields.length === 9
  const legacy = fields.length === 16 || fields.length === 17
  if (!modern && !legacy) return fail('UNSUPPORTED_FORMAT')
  if (fields.some((field) => /[\x00-\x1f\x7f-\x9f]/u.test(field))) return fail('INVALID_DATA')

  const doc = fields[modern ? 4 : 1]
  const lastName = fields[modern ? 1 : 4]
  const firstName = fields[modern ? 2 : 5]
  const sex = fields[modern ? 3 : 8]
  const dates = modern ? [6, 7] : [7, 9, 12]
  const anchorsValid = /^[MFX]$/.test(sex) && dates.every((index) => validDate(fields[index]))
    && (!modern || (/^\d{11}$/.test(fields[0]) && /^[A-Z]$/.test(fields[5])
      && (fields.length === 8 || /^\d{3}$/.test(fields[8]))))
  // Accept only unpunctuated or conventionally grouped 7–8 digit documents.
  const documentNumber = doc.replaceAll('.', '')
  if (!anchorsValid || !/^(?:\d{7,8}|\d{1,2}\.\d{3}\.\d{3})$/.test(doc)
    || /^0+$/.test(documentNumber) || !validName(lastName) || !validName(firstName)) {
    return fail('INVALID_DATA')
  }
  return { ok: true, data: { documentNumber, lastName, firstName }, format: modern ? 'pdf417-modern' : 'pdf417-legacy' }
}
